import type { EligibilityStatus } from './eligibility'
import { type SecondDriverHandover, secondDriverNeedsOwner } from './second-driver'
import type { CargosTransmissionStatus } from './vocabulary'

/**
 * OPS-4: what stops a hand-over. Two kinds, by S6's decisions:
 *
 * - **Hard**: the Owner cannot override them. Decision 1 (Fady, 15 Oct): the
 *   CaRGOS block clears only with a receipt. Decision 5: the renter's identity,
 *   home address included, is recorded at every hand-over; Q-162: so is the
 *   live second driver's (`second_identity`).
 * - **Overridable**: the Owner may let the vehicle go anyway, with a reason
 *   (as before S6):
 *   - an ineligible driver;
 *   - a birth date that differs from the booking's;
 *   - no contract, or only paper where the iPad's is required;
 *   - a deposit not held where one is required;
 *   - Q-162: a second driver ineligible at the hand-over's dates with no
 *     reason given when added, or whose ID's birth date differs
 *     (`second_driver`).
 */
export const HARD_CHECKOUT_BLOCKS = ['identity', 'cargos', 'second_identity'] as const
export type HardCheckoutBlock = (typeof HARD_CHECKOUT_BLOCKS)[number]

export const OVERRIDABLE_CHECKOUT_BLOCKS = [
  'eligibility',
  'birth_date',
  'contract',
  'deposit',
  'second_driver',
] as const
export type OverridableCheckoutBlock = (typeof OVERRIDABLE_CHECKOUT_BLOCKS)[number]

export type CheckoutBlock = HardCheckoutBlock | OverridableCheckoutBlock

export const isHardCheckoutBlock = (block: CheckoutBlock): block is HardCheckoutBlock =>
  (HARD_CHECKOUT_BLOCKS as readonly string[]).includes(block)

/**
 * How the customer signed: on the iPad, for this booking as it stands and the
 * vehicle leaving (CTR-2); on paper; or not at all.
 */
export type ContractEvidence = 'digital' | 'paper' | 'none'

/**
 * Whether the latest CaRGOS report covers the hand-over about to happen.
 * - `ok`: yes.
 * - `none`: no report yet.
 * - `other_vehicle`: it reported another car (a swap since).
 * - `booking_changed`: it reported another version of the booking.
 * - `data_changed`: the identity was recorded again since.
 */
export const RECEIPT_COVERS = [
  'ok',
  'none',
  'other_vehicle',
  'booking_changed',
  'data_changed',
] as const
export type ReceiptCover = (typeof RECEIPT_COVERS)[number]

export interface CargosReport {
  status: CargosTransmissionStatus
  vehicleId: string
  reservationVersion: number
  identityId: string
  secondIdentityId: string | null
}

export interface HandoverFacts {
  vehicleId: string
  reservationVersion: number
  /** The booking's latest identities, as recorded at the counter. */
  identityId: string | null
  secondIdentityId: string | null
}

/**
 * Whether a report covers this hand-over: the same car, the same version of
 * the booking, the identities as they stand. Only a receipt counts: CaRGOS
 * sent it back, or it was typed from the portal or the PEC. The dry run
 * (`checked`) never does.
 */
export function receiptCovers(latest: CargosReport | null, now: HandoverFacts): ReceiptCover {
  if (!latest || (latest.status !== 'sent' && latest.status !== 'recorded')) return 'none'
  if (latest.vehicleId !== now.vehicleId) return 'other_vehicle'
  if (latest.reservationVersion !== now.reservationVersion) return 'booking_changed'
  if (latest.identityId !== now.identityId || latest.secondIdentityId !== now.secondIdentityId) {
    return 'data_changed'
  }
  return 'ok'
}

/**
 * Of a booking's reports, newest first: the receipt that covers this
 * hand-over, else why the newest receipt does not. Any covering receipt
 * counts, not only the newest: after a swap and a swap back, CaRGOS already
 * holds the report for this car, and a second one would duplicate it.
 */
export function coveringReport<T extends CargosReport>(
  reports: readonly T[],
  now: HandoverFacts,
): { report: T | null; cover: ReceiptCover } {
  const receipts = reports.filter(
    (report) => report.status === 'sent' || report.status === 'recorded',
  )
  const covering = receipts.find((report) => receiptCovers(report, now) === 'ok')
  if (covering) return { report: covering, cover: 'ok' }
  return { report: null, cover: receiptCovers(receipts[0] ?? null, now) }
}

export interface CheckoutBlockFacts {
  eligibilityStatus: EligibilityStatus
  /** Decision 5: an identity recorded at the counter, with the home address. */
  identityRecorded: boolean
  /** The identity's birth date is not the one the booking was assessed with. */
  birthDateDiffers: boolean
  /** The category requires CaRGOS and `cargos.enabled` is on. */
  cargosRequired: boolean
  cargosCover: ReceiptCover
  contract: ContractEvidence
  /** The setting `contracts.digital_required`: then paper alone needs the Owner. */
  contractRequired: boolean
  /** Q-63, the setting `deposits.required`: a deposit must be held. */
  depositRequired: boolean
  depositHeld: boolean
  /** Q-162: the booking's live second driver; null when it has none. */
  secondDriver: SecondDriverHandover | null
}

export interface CheckoutBlocks {
  blocks: CheckoutBlock[]
  /** The ones the Owner cannot override. */
  hard: HardCheckoutBlock[]
}

export function checkoutBlocks(facts: CheckoutBlockFacts): CheckoutBlocks {
  const blocks: CheckoutBlock[] = []
  if (!facts.identityRecorded) blocks.push('identity')
  if (facts.cargosRequired && facts.cargosCover !== 'ok') blocks.push('cargos')
  if (facts.eligibilityStatus === 'ineligible') blocks.push('eligibility')
  if (facts.identityRecorded && facts.birthDateDiffers) blocks.push('birth_date')
  if (facts.contract === 'none' || (facts.contract === 'paper' && facts.contractRequired)) {
    blocks.push('contract')
  }
  if (facts.depositRequired && !facts.depositHeld) blocks.push('deposit')
  if (facts.secondDriver) {
    if (!facts.secondDriver.identityRecorded) blocks.push('second_identity')
    if (secondDriverNeedsOwner(facts.secondDriver)) blocks.push('second_driver')
  }
  return { blocks, hard: blocks.filter(isHardCheckoutBlock) }
}
